/**
 * Product Finder Web Component
 * A four-step wizard for finding products based on gender, height, color, and extra benefit preferences.
 * Uses Shopify's storefront filtering system.
 */

if (!customElements.get('product-finder')) {
  class ProductFinder extends HTMLElement {
    constructor() {
      super();
      this.resultsVisible = false;
      this.currentFilters = {};
      this.abortController = null;
      this.productCount = 0;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    init() {
      // Get configuration
      const configEl = this.querySelector(`script[type="application/json"]`);
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Product Finder: Failed to parse config', e);
          return;
        }
      }

      // Cache DOM elements
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[id^="ProductFinderSubmit"]');
      this.submitText = this.submitBtn?.querySelector('.product-finder__submit-text');
      this.resultsContainer = this.querySelector('[id^="ProductFinderResults"]');
      this.resultsGrid = this.querySelector('[id^="ProductFinderGrid"]');
      this.resultsEmpty = this.querySelector('.product-finder__results-empty');
      this.resultsLoading = this.querySelector('.product-finder__results-loading');
      this.lineOverview = this.querySelector('[id^="ProductFinderLineOverview"]');
      this.lineTableWrapper = this.lineOverview?.querySelector('.product-finder__line-table-wrapper');
      this.lineOverviewEmpty = this.lineOverview?.querySelector('.product-finder__line-overview-empty');

      // Bind event handlers
      this.handleFormChange = this.handleFormChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSelectAll = this.handleSelectAll.bind(this);
      this.handleGenderChange = this.handleGenderChange.bind(this);

      // Add event listeners
      this.form?.addEventListener('change', this.handleFormChange);
      this.submitBtn?.addEventListener('click', this.handleSubmit);

      // Handle "select all" checkbox for colors
      this.querySelectorAll('[data-select-all="true"]').forEach(checkbox => {
        checkbox.addEventListener('change', this.handleSelectAll);
      });

      // Handle gender checkboxes - only allow one selection at a time
      this.querySelectorAll('.product-finder__checkbox--gender').forEach(checkbox => {
        checkbox.addEventListener('change', this.handleGenderChange);
      });

      // Initial count fetch
      this.updateProductCount();
    }

    /**
     * Handles gender checkbox changes - ensures only one gender is selected at a time
     * @param {Event} event - The change event
     */
    handleGenderChange(event) {
      const checkbox = event.target;
      
      // If this checkbox was just checked, uncheck all other gender checkboxes
      if (checkbox.checked) {
        this.querySelectorAll('.product-finder__checkbox--gender').forEach(other => {
          if (other !== checkbox) {
            other.checked = false;
          }
        });
      }
    }

    /**
     * Handles form input changes (radio/checkbox selections)
     * @param {Event} event - The change event
     */
    handleFormChange(event) {
      const target = event.target;
      
      // Handle "select all" colors checkbox
      if (target.dataset.selectAll === 'true') {
        return; // Handled by separate listener
      }

      // If a color checkbox is checked, uncheck "all colors"
      if (target.name === this.config.filters.color && target.checked) {
        const selectAllCheckbox = this.querySelector('[data-select-all="true"]');
        if (selectAllCheckbox && selectAllCheckbox.checked) {
          selectAllCheckbox.checked = false;
        }
      }

      // Update current filters from form
      this.updateFiltersFromForm();

      // Update product count
      this.updateProductCount();

      // If results are already visible, update them
      if (this.resultsVisible) {
        this.fetchAndRenderProducts();
      }
    }

    /**
     * Handles the "select all colors" checkbox
     * @param {Event} event - The change event
     */
    handleSelectAll(event) {
      const isChecked = event.target.checked;
      const colorCheckboxes = this.querySelectorAll(
        `input[name="${this.config.filters.color}"]:not([data-select-all="true"])`
      );

      // Uncheck all other color checkboxes when "all" is selected
      if (isChecked) {
        colorCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
      }

      this.updateFiltersFromForm();
      this.updateProductCount();

      if (this.resultsVisible) {
        this.fetchAndRenderProducts();
      }
    }

    /**
     * Updates the internal filter state from form inputs
     */
    updateFiltersFromForm() {
      this.currentFilters = {};

      // Gender (checkbox with single-select behavior)
      // When boys or girls is selected, also include unisex products
      const genderInput = this.form.querySelector(`input[name="${this.config.filters.gender}"]:checked`);
      if (genderInput) {
        const selectedGender = genderInput.value;
        const genderValues = this.config.genderValues;
        
        // If boy or girl is selected, include both the selected gender AND unisex
        if (genderValues && (selectedGender === genderValues.boy || selectedGender === genderValues.girl)) {
          this.currentFilters[this.config.filters.gender] = [selectedGender, genderValues.unisex];
        } else {
          // For unisex selection (or any other), just use the selected value
          this.currentFilters[this.config.filters.gender] = [selectedGender];
        }
      }

      // Height (checkboxes - multiple selections allowed)
      const heightInputs = this.form.querySelectorAll(`input[name="${this.config.filters.height}"]:checked`);
      const heights = Array.from(heightInputs).map(input => input.value);
      if (heights.length > 0) {
        this.currentFilters[this.config.filters.height] = heights;
      }

      // Colors (checkboxes)
      const selectAllColors = this.querySelector('[data-select-all="true"]');
      if (!selectAllColors?.checked) {
        const colorInputs = this.form.querySelectorAll(`input[name="${this.config.filters.color}"]:checked:not([data-select-all="true"])`);
        const colors = Array.from(colorInputs).map(input => input.value);
        if (colors.length > 0) {
          this.currentFilters[this.config.filters.color] = colors;
        }
      }
      // If "all colors" is checked, we don't add any color filter (show all)

      // Extra benefits (boolean metafields - only filter true values)
      const extraFilters = this.config.filters.extras || [];
      extraFilters.forEach(filterParam => {
        const extraInput = this.form.querySelector(`input[name="${filterParam}"]:checked`);
        if (extraInput) {
          this.currentFilters[filterParam] = [extraInput.value];
        }
      });
    }

    /**
     * Builds the filter URL parameters
     * @returns {URLSearchParams}
     */
    buildFilterParams() {
      const params = new URLSearchParams();

      for (const [key, values] of Object.entries(this.currentFilters)) {
        values.forEach(value => {
          params.append(key, value);
        });
      }

      return params;
    }

    /**
     * Handles the submit button click
     */
    async handleSubmit() {
      this.updateFiltersFromForm();
      
      // Show results container
      if (!this.resultsVisible) {
        this.resultsContainer.hidden = false;
        this.resultsVisible = true;
      }

      // Fetch and render products
      await this.fetchAndRenderProducts();

      // Scroll to results
      this.scrollToResults();
    }

    /**
     * Fetches the product count for current filters
     */
    async updateProductCount() {
      this.updateFiltersFromForm();
      
      const params = this.buildFilterParams();
      const url = `${this.config.collectionUrl}?${params.toString()}&section_id=${this.config.sectionId}`;

      try {
        // Cancel previous request
        if (this.countAbortController) {
          this.countAbortController.abort();
        }
        this.countAbortController = new AbortController();

        const response = await fetch(url, {
          signal: this.countAbortController.signal
        });

        if (response.ok) {
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Try to find product count from the response
          // Look for product grid items
          const productGrid = doc.querySelector('[id^="ProductFinderGrid"]');
          if (productGrid) {
            this.productCount = this.getProductCountFromGrid(productGrid);
            this.updateLineOverview(productGrid);
          } else {
            // Fallback: count based on pagination or other indicators
            this.productCount = this.extractProductCount(html);
            this.updateLineOverview();
          }

          this.updateSubmitButtonText();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Product Finder: Failed to update count', error);
        }
      }
    }

    /**
     * Extracts product count from HTML response
     * @param {string} html - The HTML response
     * @returns {number}
     */
    extractProductCount(html) {
      // Try to extract count from various sources
      const countMatch = html.match(/data-num-results="(\d+)"/);
      if (countMatch) {
        return parseInt(countMatch[1], 10);
      }

      // Fallback: count product cards
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const products = doc.querySelectorAll('.product-card');
      return products.length;
    }

    /**
     * Gets the total filtered product count from the rendered grid.
     * @param {Element} productGrid - Product grid element from the fetched HTML
     * @returns {number}
     */
    getProductCountFromGrid(productGrid) {
      const count = parseInt(productGrid?.dataset.productCount, 10);
      if (!Number.isNaN(count)) {
        return count;
      }

      return productGrid?.querySelectorAll('.product-card').length || 0;
    }

    /**
     * Checks whether any product finder filters are active.
     * @returns {boolean}
     */
    hasActiveFilters() {
      return Object.values(this.currentFilters).some(values => values.length > 0);
    }

    /**
     * Splits product-line tag strings from Liquid data attributes.
     * @param {string} value - Raw tag string
     * @returns {string[]}
     */
    parseProductLineTags(value) {
      if (!value) return [];

      return value
        .split(/[|,\n]+/)
        .map(tag => tag.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    }

    /**
     * Collects product-line tags from rendered product cards.
     * @param {Element} productGrid - Product grid element
     * @returns {Set<string>}
     */
    getProductLineTagsFromGrid(productGrid) {
      const tags = new Set();
      productGrid?.querySelectorAll('[data-product-line-tags]').forEach(card => {
        this.parseProductLineTags(card.dataset.productLineTags).forEach(tag => {
          tags.add(tag);
        });
      });

      return tags;
    }

    /**
     * Shows matching product-line table rows for the current filters.
     * @param {Element} productGrid - Product grid from fetched HTML
     */
    updateLineOverview(productGrid = this.resultsGrid) {
      if (!this.lineOverview) return;

      if (!this.hasActiveFilters()) {
        this.lineOverview.hidden = true;
        return;
      }

      const rows = Array.from(this.lineOverview.querySelectorAll('[data-product-line-row]'));
      if (rows.length === 0) {
        this.lineOverview.hidden = true;
        return;
      }

      const totalCount = parseInt(productGrid?.dataset.productCount, 10);
      const productLimit = parseInt(productGrid?.dataset.productLimit, 10);
      const showAllRows = !Number.isNaN(totalCount) && !Number.isNaN(productLimit) && totalCount >= productLimit;
      const activeTags = showAllRows ? null : this.getProductLineTagsFromGrid(productGrid);
      let visibleRows = 0;

      rows.forEach(row => {
        const rowTags = this.parseProductLineTags(row.dataset.productLineTags);
        const shouldShow = showAllRows || rowTags.some(tag => activeTags.has(tag));
        row.hidden = !shouldShow;

        if (shouldShow) {
          visibleRows += 1;
        }
      });

      this.lineOverview.hidden = false;

      if (this.lineTableWrapper) {
        this.lineTableWrapper.hidden = visibleRows === 0;
      }

      if (this.lineOverviewEmpty) {
        this.lineOverviewEmpty.hidden = visibleRows > 0;
      }
    }

    /**
     * Updates the submit button text with product count
     */
    updateSubmitButtonText() {
      if (!this.submitText) return;

      const productLimit = parseInt(this.config.productLimit, 10);
      const isOverLimit = !Number.isNaN(productLimit) && this.productCount > productLimit;
      const displayCount = isOverLimit ? `${productLimit}+` : this.productCount;
      let template;
      if (isOverLimit) {
        template = this.config.translations.showProductsPlural || this.config.translations.showProducts;
      } else if (this.productCount === 1) {
        template = this.config.translations.showProducts;
      } else if (this.productCount >= 2 && this.productCount <= 4) {
        template = this.config.translations.showProducts2_4 || this.config.translations.showProductsPlural || this.config.translations.showProducts;
      } else {
        // 0 or 5+ products
        template = this.config.translations.showProductsPlural || this.config.translations.showProducts;
      }
      
      this.submitText.textContent = template.replace('{count}', displayCount);
    }

    /**
     * Fetches and renders products based on current filters
     */
    async fetchAndRenderProducts() {
      const params = this.buildFilterParams();
      const url = `${this.config.collectionUrl}?${params.toString()}&section_id=${this.config.sectionId}`;

      // Show loading state
      this.setLoadingState(true);

      try {
        // Cancel previous request
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        const response = await fetch(url, {
          signal: this.abortController.signal
        });

        if (response.ok) {
          const html = await response.text();
          this.renderProducts(html);
          this.updateBrowserHistory(params);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Product Finder: Failed to fetch products', error);
          this.showError();
        }
      } finally {
        this.setLoadingState(false);
      }
    }

    /**
     * Renders products from HTML response
     * @param {string} html - The HTML response
     */
    renderProducts(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid in the response
      const newGrid = doc.querySelector('[id^="ProductFinderGrid"]');
      let overviewGrid = newGrid;
      
      if (newGrid && newGrid.children.length > 0) {
        this.resultsGrid.innerHTML = newGrid.innerHTML;
        this.resultsGrid.dataset.productCount = newGrid.dataset.productCount || '';
        this.resultsGrid.dataset.productLimit = newGrid.dataset.productLimit || '';
        this.resultsEmpty.hidden = true;
        this.productCount = this.getProductCountFromGrid(newGrid);
      } else {
        // Try to extract products from a standard collection template
        const productCards = doc.querySelectorAll('.product-card');
        if (productCards.length > 0) {
          this.resultsGrid.innerHTML = '';
          const gridClass = this.config.gridSettings?.grid || 'col-st-6 col-3';
          const mobileClass = this.config.gridSettings?.mobileGrid || '6';
          productCards.forEach(card => {
            const wrapper = document.createElement('div');
            wrapper.className = `col-sp-${mobileClass} ${gridClass}`;
            wrapper.appendChild(card.cloneNode(true));
            this.resultsGrid.appendChild(wrapper);
          });
          this.resultsEmpty.hidden = true;
          this.productCount = productCards.length;
          overviewGrid = this.resultsGrid;
        } else {
          // No products found
          this.resultsGrid.innerHTML = '';
          this.resultsEmpty.hidden = false;
          this.productCount = 0;
          overviewGrid = newGrid || this.resultsGrid;
        }
      }

      this.updateSubmitButtonText();
      this.updateLineOverview(overviewGrid);

      // Re-initialize any necessary JS (like quick add modals)
      this.reinitializeComponents();
    }

    /**
     * Re-initializes JavaScript components after DOM update
     */
    reinitializeComponents() {
      // Dispatch event for other components to hook into
      document.dispatchEvent(new CustomEvent('product-finder:products-updated', {
        bubbles: true,
        detail: { container: this.resultsGrid }
      }));

      // Re-initialize lazy loading if available
      if (typeof initLazyImages === 'function') {
        initLazyImages();
      }
    }

    /**
     * Sets the loading state of the component
     * @param {boolean} isLoading - Whether the component is loading
     */
    setLoadingState(isLoading) {
      this.submitBtn?.classList.toggle('is-loading', isLoading);
      this.resultsContainer?.classList.toggle('is-loading', isLoading);
      
      if (this.resultsLoading) {
        this.resultsLoading.hidden = !isLoading;
      }

      if (this.resultsGrid) {
        this.resultsGrid.style.opacity = isLoading ? '0.5' : '1';
      }
    }

    /**
     * Shows an error state
     */
    showError() {
      this.resultsGrid.innerHTML = `<p class="product-finder__error">${this.config.translations.noProducts}</p>`;
    }

    /**
     * Updates browser history with current filter state
     * @param {URLSearchParams} params - The filter parameters
     */
    updateBrowserHistory(params) {
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({ productFinderFilters: this.currentFilters }, '', newUrl);
    }

    /**
     * Scrolls to the results section
     */
    scrollToResults() {
      if (!this.resultsContainer) return;

      const headerOffset = 100; // Adjust for sticky header
      const elementPosition = this.resultsContainer.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  customElements.define('product-finder', ProductFinder);
}

/**
 * Debounce utility function
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function}
 */
function debounce(fn, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
